import { getCountries, type CountryCode } from "libphonenumber-js"
import { z } from "zod"

import { normalizeMetaTestPhoneNumber } from "./phone"

export const META_GRAPH_API_VERSION = "v26.0"
export const META_ADS_OAUTH_SESSION_MINUTES = 15
export const META_ADS_REQUIRED_SCOPES = [
  "pages_show_list",
  "pages_read_engagement",
  "pages_manage_metadata",
  "leads_retrieval",
  "business_management",
  "ads_management",
  "pages_manage_ads",
] as const

const supportedCountryCodes = new Set<string>(getCountries())

export const MetaObjectIdSchema = z.string().regex(/^\d{1,100}$/)
export const MetaAppOrPageIdSchema = z.string().regex(/^\d{1,50}$/)
export const MetaLeadFieldKeySchema = z.string().regex(/^[A-Za-z0-9_]{1,100}$/)
export const MetaCountryCodeSchema = z.string()
  .regex(/^[A-Z]{2}$/)
  .refine(value => supportedCountryCodes.has(value), "Unsupported country code")
  .transform(value => value as CountryCode)

export const MetaAdsIntegrationInputSchema = z.object({
  voiceAgentId: z.string().uuid().nullable().default(null),
  callerPhoneNumber: z.string().regex(/^\+[1-9]\d{7,14}$/).nullable().default(null),
  phoneFieldKey: MetaLeadFieldKeySchema.default("phone_number"),
  serviceInterestFieldKey: MetaLeadFieldKeySchema.nullable().default(null),
  defaultPhoneCountry: MetaCountryCodeSchema,
  respectWorkingHours: z.boolean().default(true),
}).superRefine((value, context) => {
  if (Boolean(value.voiceAgentId) !== Boolean(value.callerPhoneNumber)) {
    context.addIssue({ code: "custom", path: ["voiceAgentId"], message: "Choose both a Voice Agent and a caller number, or leave both empty" })
  }
})

export const MetaAdsOAuthSessionIdSchema = z.string().uuid()
export const MetaAdsFormScopeSchema = z.enum(["all", "selected"])

const MetaAdsFormSelectionSchema = z.object({
  formScope: MetaAdsFormScopeSchema,
  selectedFormIds: z.array(MetaObjectIdSchema).max(100),
}).superRefine((value, context) => {
  if (value.formScope === "all" && value.selectedFormIds.length > 0) {
    context.addIssue({
      code: "custom",
      path: ["selectedFormIds"],
      message: "All forms cannot include selected form IDs",
    })
  }
  if (value.formScope === "selected" && value.selectedFormIds.length === 0) {
    context.addIssue({
      code: "custom",
      path: ["selectedFormIds"],
      message: "Select at least one Instant Form",
    })
  }
})

export const MetaAdsCompleteOAuthInputSchema = z.object({
  sessionId: MetaAdsOAuthSessionIdSchema,
  pageId: MetaAppOrPageIdSchema,
  adAccountId: MetaObjectIdSchema,
  phoneFieldKey: MetaLeadFieldKeySchema,
}).and(MetaAdsFormSelectionSchema)

export const MetaAdsCreateFormCopyInputSchema = z.object({
  sourceFormId: MetaObjectIdSchema,
  name: z.string().trim().min(1).max(255),
})

export const MetaAdsAcknowledgeFormHandoffInputSchema = z.object({
  copiedFormId: MetaObjectIdSchema,
})

export const MetaAdsTestLeadInputSchema = z.object({
  phoneNumber: z.string()
    .transform(value => normalizeMetaTestPhoneNumber(value) ?? value.trim())
    .pipe(z.string().regex(/^\+[1-9]\d{7,14}$/)),
})

const MetaFollowUpBase = {
  instructions: z.string().trim().min(1).max(2000),
  respectWorkingHours: z.boolean(),
  delayMinutes: z.number().int().min(0).max(10_080).default(0),
}

export const MetaFollowUpActionSchema = z.discriminatedUnion("actionType", [
  z.object({
    actionType: z.literal("schedule_call"),
    voiceAgentId: z.string().uuid(),
    callerPhoneNumber: z.string().regex(/^\+[1-9]\d{7,14}$/),
    phoneFieldKey: MetaLeadFieldKeySchema,
    ...MetaFollowUpBase,
  }),
  z.object({
    actionType: z.literal("send_email"),
    emailAgentId: z.string().uuid(),
    inboxId: z.string().uuid(),
    emailFieldKey: MetaLeadFieldKeySchema,
    subject: z.string().trim().min(1).max(255),
    businessName: z.string().trim().min(1).max(255),
    disclosureText: z.string().trim().min(1).max(10000),
    ...MetaFollowUpBase,
  }),
  z.object({
    actionType: z.literal("send_whatsapp"),
    messageAgentId: z.string().uuid(),
    channelId: z.string().uuid(),
    phoneFieldKey: MetaLeadFieldKeySchema,
    templateName: z.string().trim().min(1).max(255),
    templateLanguage: z.string().trim().min(2).max(35),
    templateParameterFieldKeys: z.array(MetaLeadFieldKeySchema).max(20),
    businessName: z.string().trim().min(1).max(255),
    disclosureText: z.string().trim().min(1).max(10000),
    ...MetaFollowUpBase,
  }),
])

export const MetaFormFollowUpActionsInputSchema = z.object({
  formId: MetaObjectIdSchema,
  // Ordered: one primary action, then an optional failure-only fallback.
  actions: z.array(MetaFollowUpActionSchema).max(2),
}).superRefine((value, context) => {
  const [primary, fallback] = value.actions
  if (primary && fallback && primary.actionType === fallback.actionType && fallback.delayMinutes === 0) {
    context.addIssue({ code: "custom", path: ["actions", 1, "delayMinutes"], message: "Delay a repeat of the same action by at least one minute" })
  }
})

export type MetaFollowUpAction = z.infer<typeof MetaFollowUpActionSchema>
export type MetaFormFollowUpActionsInput = z.infer<typeof MetaFormFollowUpActionsInputSchema>

export const MetaLeadgenValueSchema = z.object({
  leadgen_id: MetaObjectIdSchema,
  page_id: MetaAppOrPageIdSchema,
  form_id: MetaObjectIdSchema.optional(),
  ad_id: MetaObjectIdSchema.optional(),
  adgroup_id: MetaObjectIdSchema.optional(),
  created_time: z.number().int().positive().optional(),
})

export const MetaLeadgenWebhookSchema = z.object({
  object: z.literal("page"),
  entry: z.array(z.object({
    id: MetaAppOrPageIdSchema,
    changes: z.array(z.object({
      field: z.string().max(100),
      value: z.unknown(),
    })).max(100).optional().default([]),
  })).min(1).max(100),
})

export const MetaLeadFieldDataSchema = z.object({
  name: z.string().trim().min(1).max(100),
  values: z.array(z.string().max(2000)).max(20),
})

export const MetaLeadSchema = z.object({
  id: MetaObjectIdSchema,
  created_time: z.string().max(100).optional(),
  ad_id: MetaObjectIdSchema.optional(),
  ad_name: z.string().trim().min(1).max(255).optional(),
  adset_id: MetaObjectIdSchema.optional(),
  adset_name: z.string().trim().min(1).max(255).optional(),
  campaign_id: MetaObjectIdSchema.optional(),
  campaign_name: z.string().trim().min(1).max(255).optional(),
  form_id: MetaObjectIdSchema.optional(),
  field_data: z.array(MetaLeadFieldDataSchema).max(100),
})

export const MetaLeadFormSummarySchema = z.object({
  id: MetaObjectIdSchema,
  name: z.string().trim().min(1).max(255),
})

export const MetaPageSchema = z.object({
  id: MetaAppOrPageIdSchema,
  name: z.string().trim().min(1).max(255),
})

export const MetaOAuthPageSchema = MetaPageSchema.extend({
  access_token: z.string().trim().min(1).max(4000),
  tasks: z.array(z.string().max(100)).max(100).default([]),
})

export const MetaOAuthPagesResponseSchema = z.object({
  data: z.array(MetaOAuthPageSchema).max(500),
})

export const MetaAdAccountSchema = z.object({
  account_id: MetaObjectIdSchema,
  name: z.string().trim().min(1).max(255),
  business: z.object({
    id: MetaObjectIdSchema,
    name: z.string().trim().min(1).max(255),
  }).nullable().optional().default(null),
})

export const MetaAdAccountsResponseSchema = z.object({
  data: z.array(MetaAdAccountSchema).max(500),
})

export const MetaBusinessesResponseSchema = z.object({
  data: z.array(z.object({
    id: MetaObjectIdSchema,
  })).max(500),
})

export const MetaLeadQuestionOptionSchema = z.object({
  key: z.string().trim().min(1).max(500).optional(),
  value: z.string().trim().min(1).max(500).optional(),
})

export const MetaLeadQuestionSchema = z.object({
  key: MetaLeadFieldKeySchema.optional(),
  label: z.string().trim().min(1).max(1000).optional(),
  type: z.string().trim().min(1).max(100),
  inline_context: z.string().trim().min(1).max(2000).optional(),
  options: z.array(MetaLeadQuestionOptionSchema).max(200).default([]),
  conditional_questions_choices: z.array(z.unknown()).max(200).optional(),
  dependent_conditional_questions: z.array(z.unknown()).max(200).optional(),
})

export const MetaLeadFormSchema = z.object({
  id: MetaObjectIdSchema,
  name: z.string().trim().min(1).max(255),
  status: z.enum(["ACTIVE", "ARCHIVED", "DELETED", "DRAFT"]),
  allow_organic_lead: z.boolean().nullable().default(null),
  block_display_for_non_targeted_viewer: z.boolean().nullable().default(null),
  questions: z.array(MetaLeadQuestionSchema).max(100).default([]),
})

export const MetaLeadFormsResponseSchema = z.object({
  data: z.array(MetaLeadFormSchema).max(500),
})

const MetaLeadPrivacyPolicySchema = z.object({
  url: z.string().trim().min(1).max(2048),
  link_text: z.string().trim().min(1).max(70).optional(),
})

const MetaLeadCustomDisclaimerSchema = z.object({
  title: z.string().trim().min(1).max(500).optional(),
  body: z.object({
    text: z.string().trim().min(1).max(10_000),
    url_entities: z.array(z.object({
      offset: z.number().int().nonnegative(),
      length: z.number().int().nonnegative(),
      url: z.string().trim().min(1).max(2048),
    })).max(100).default([]),
  }).optional(),
  checkboxes: z.array(z.object({
    key: z.string().trim().min(1).max(500),
    text: z.string().trim().min(1).max(2000),
    is_required: z.boolean().optional(),
    is_checked_by_default: z.boolean().optional(),
  })).max(100).default([]),
})

const MetaLeadContextCardSchema = z.object({
  button_text: z.string().trim().min(1).max(500).optional(),
  content: z.array(z.string().trim().min(1).max(2000)).max(100).default([]),
  style: z.string().trim().min(1).max(100).optional(),
  title: z.string().trim().min(1).max(500).optional(),
})

const MetaLeadThankYouPageSchema = z.object({
  body: z.string().trim().min(1).max(2000).optional(),
  business_phone_number: z.string().trim().min(1).max(100).optional(),
  button_text: z.string().trim().min(1).max(500).optional(),
  button_type: z.string().trim().min(1).max(100).optional(),
  country_code: z.string().trim().min(1).max(10).optional(),
  enable_messenger: z.boolean().optional(),
  lead_gen_use_case: z.string().trim().min(1).max(100).optional(),
  title: z.string().trim().min(1).max(500).optional(),
  website_url: z.string().trim().min(1).max(2048).optional(),
  gated_file: z.unknown().optional(),
})

export const MetaLeadFormCopySourceSchema = MetaLeadFormSchema.extend({
  locale: z.string().trim().min(1).max(100),
  context_card: MetaLeadContextCardSchema.nullable().default(null),
  legal_content: z.object({
    privacy_policy: MetaLeadPrivacyPolicySchema.nullable().default(null),
    custom_disclaimer: MetaLeadCustomDisclaimerSchema.nullable().default(null),
  }).nullable().default(null),
  privacy_policy_url: z.string().trim().min(1).max(2048).nullable().default(null),
  thank_you_page: MetaLeadThankYouPageSchema.nullable().default(null),
  follow_up_action_url: z.string().trim().min(1).max(2048).nullable().default(null),
  question_page_custom_headline: z.string().trim().min(1).max(500).nullable().default(null),
  is_optimized_for_quality: z.boolean().nullable().default(null),
})

export const MetaCreatedObjectSchema = z.object({
  id: MetaObjectIdSchema,
})

export const MetaLeadIdsResponseSchema = z.object({
  data: z.array(z.object({ id: MetaObjectIdSchema })).max(1),
})

export const MetaSubscribedAppsSchema = z.object({
  data: z.array(z.object({
    id: MetaAppOrPageIdSchema,
    subscribed_fields: z.array(z.string().max(100)).max(100).default([]),
  })).max(100),
})

export const MetaAccessTokenResponseSchema = z.object({
  access_token: z.string().trim().min(1).max(4000),
  token_type: z.string().max(100).optional(),
  expires_in: z.number().int().nonnegative().optional(),
})

export const MetaDebugTokenResponseSchema = z.object({
  data: z.object({
    app_id: MetaAppOrPageIdSchema,
    type: z.string().max(100).optional(),
    is_valid: z.boolean(),
    expires_at: z.number().int().nonnegative().optional(),
    data_access_expires_at: z.number().int().nonnegative().optional(),
    scopes: z.array(z.string().max(100)).max(200).default([]),
    granular_scopes: z.array(z.object({
      scope: z.string().max(100),
      target_ids: z.array(MetaObjectIdSchema).max(500).optional(),
    })).max(200).optional(),
  }),
})

export type MetaAdsIntegrationInput = z.infer<
  typeof MetaAdsIntegrationInputSchema
>
export type MetaLeadgenWebhook = z.infer<typeof MetaLeadgenWebhookSchema>
export type MetaLeadgenValue = z.infer<typeof MetaLeadgenValueSchema>
export type MetaLead = z.infer<typeof MetaLeadSchema>
export type MetaLeadFormSummary = z.infer<typeof MetaLeadFormSummarySchema>
export type MetaPage = z.infer<typeof MetaPageSchema>
export type MetaOAuthPage = z.infer<typeof MetaOAuthPageSchema>
export type MetaAdAccount = z.infer<typeof MetaAdAccountSchema>
export type MetaLeadForm = z.infer<typeof MetaLeadFormSchema>
export type MetaLeadQuestion = z.infer<typeof MetaLeadQuestionSchema>
export type MetaLeadFormCopySource = z.infer<typeof MetaLeadFormCopySourceSchema>
export type MetaAdsFormScope = z.infer<typeof MetaAdsFormScopeSchema>
export type MetaAdsFormSelection = z.infer<typeof MetaAdsFormSelectionSchema>

export type MetaLeadAccessStatus = "ready" | "action_required" | "unverified"
export type MetaAdsFormsReadiness = {
  forms: MetaLeadForm[]
  leadAccessStatus: MetaLeadAccessStatus
  leadAccessErrorCode: string | null
}

export type MetaLeadFormCreateQuestion = {
  type: string
  key?: string
  label?: string
  inline_context?: string
  options?: Array<{ key?: string; value?: string }>
}

export type MetaLeadFormCreateInput = {
  name: string
  locale: string
  questions: MetaLeadFormCreateQuestion[]
  privacyPolicy: { url: string; link_text?: string }
  allowOrganicLeadRetrieval: true
  blockDisplayForNonTargetedViewer: false
  contextCard?: {
    button_text?: string
    content?: string[]
    style?: string
    title?: string
  }
  customDisclaimer?: {
    title?: string
    body?: {
      text: string
      url_entities?: Array<{ offset: number; length: number; url: string }>
    }
    checkboxes?: Array<{
      key: string
      text: string
      is_required?: boolean
      is_checked_by_default?: boolean
    }>
  }
  thankYouPage?: {
    body?: string
    business_phone_number?: string
    button_text?: string
    button_type?: string
    country_code?: string
    enable_messenger?: boolean
    lead_gen_use_case?: string
    title?: string
    website_url?: string
  }
  followUpActionUrl?: string
  questionPageCustomHeadline?: string
  isOptimizedForQuality?: boolean
}

export type MetaAdsFormCopyResult = {
  sourceForm: { id: string; name: string }
  copiedForm: MetaLeadForm
  integration: MetaAdsIntegrationView
}

export type MetaAdsCompleteOAuthResult = {
  integration: MetaAdsIntegrationView
  readiness: MetaAdsFormsReadiness
}

export type MetaAdsIntegrationStatus = "draft" | "connected" | "error" | "disabled"
export type MetaAdsConnectionHealth =
  | "connected_tested"
  | "connected_untested"
  | "verification_pending"
  | "action_required_meta"
  | "authorization_expired"
  | "needs_attention"

export type MetaAdsFormHandoff = {
  id: string
  sourceForm: { id: string; name: string }
  copiedForm: { id: string; name: string }
  createdAt: string
  acknowledgedAt: string | null
}

export type MetaAdsConnectionTestStatus =
  | "submitted"
  | "webhook_received"
  | "lead_retrieved"
  | "task_created"
  | "failed"
  | "timed_out"

export type MetaAdsConnectionTest = {
  form_id: string
  lead_id: string
  status: MetaAdsConnectionTestStatus
  submitted_at: string
  task_id: string | null
  task_status: "scheduled" | "in_progress" | "completed" | "blocked" | "failed" | "cancelled" | null
  scheduled_for: string | null
  error_code: string | null
}

export type MetaAdsIntegrationView = {
  id: string
  status: MetaAdsIntegrationStatus
  connection_health: MetaAdsConnectionHealth
  meta_page_id: string
  meta_page_name: string
  meta_ad_account_id: string | null
  meta_ad_account_name: string | null
  meta_business_id: string | null
  meta_business_name: string | null
  ads_manager_url: string
  voice_agent_id: string | null
  voice_agent_name: string | null
  caller_phone_number: string | null
  phone_field_key: string
  service_interest_field_key: string | null
  form_scope: MetaAdsFormScope
  selected_form_ids: string[]
  default_phone_country: CountryCode
  respect_working_hours: boolean
  granted_scopes: string[]
  token_data_access_expires_at: string | null
  last_checked_at: string | null
  last_event_at: string | null
  last_task_created_at: string | null
  last_error_code: string | null
  connection_test: MetaAdsConnectionTest | null
  form_handoffs: MetaAdsFormHandoff[]
  form_action_overrides: Record<string, MetaFollowUpAction[]>
  recent_action_results: Array<{
    lead_id: string
    action_stage: "primary" | "fallback"
    action_type: MetaFollowUpAction["actionType"]
    status: "pending" | "task_created" | "ignored" | "failed"
    task_id: string | null
    error_code: string | null
  }>
}

export type MetaAdsOAuthStart = {
  authorizeUrl: string
  sessionId: string
}

export type MetaAdsOAuthPageOption = {
  id: string
  name: string
  tasks: string[]
}

export type MetaAdsOAuthAdAccountOption = {
  id: string
  name: string
  businessId: string | null
  businessName: string | null
}

export type MetaAdsSetupOptions = {
  voiceAgents: Array<{ id: string; name: string }>
  phoneNumbers: Array<{ phoneNumber: string; label: string | null }>
}
